import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Settings, MapPin, Save, RotateCcw, IndianRupee, RefreshCw, Calendar, Trash2, Plus, Clock, ChevronRight, ChevronDown } from 'lucide-react';

const AdminSettings = () => {
  const [gpsRange, setGpsRange] = useState(100);
  const [originalGpsRange, setOriginalGpsRange] = useState(100);
  const [defaultRange, setDefaultRange] = useState(100);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Salary configuration (Worker categories)
  const [salaryRates, setSalaryRates] = useState({
    sweeper: 0,
    gardener: 0,
    security: 0
  });
  const [originalSalaryRates, setOriginalSalaryRates] = useState({
    sweeper: 0,
    gardener: 0,
    security: 0
  });
  const [savingSalary, setSavingSalary] = useState(false);
  
  // Zone Staff Salary (Electrician & Supervisor)
  const [zoneStaffSalary, setZoneStaffSalary] = useState({
    electrician_salary_per_day: 0,
    supervisor_salary_per_day: 0
  });
  const [originalZoneStaffSalary, setOriginalZoneStaffSalary] = useState({
    electrician_salary_per_day: 0,
    supervisor_salary_per_day: 0
  });
  const [savingZoneStaffSalary, setSavingZoneStaffSalary] = useState(false);
  
  // Correction Permissions
  const [activePermissions, setActivePermissions] = useState([]);
  const [expiredPermissions, setExpiredPermissions] = useState([]);
  const [permissionRequests, setPermissionRequests] = useState([]);
  const [supervisorsList, setSupervisorsList] = useState([]);
  const [newPermission, setNewPermission] = useState({ supervisor_id: '', from_date: '', valid_until: '' });
  const [savingPermission, setSavingPermission] = useState(false);
  const [permissionTab, setPermissionTab] = useState('active'); // 'active' or 'expired'
  
  // Expandable sections - only one can be open at a time
  const [expandedSection, setExpandedSection] = useState('gps');
  
  const toggleSection = (section) => {
    setExpandedSection(prev => prev === section ? null : section);
  };
  
  const { user, userType, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  // Auth check only
  useEffect(() => {
    if (authLoading) return;
    if (!user || userType !== 'admin') {
      navigate('/admin/login');
    }
  }, [authLoading, user, userType, navigate]);

  // Initial fetch - runs once
  useEffect(() => {
    if (authLoading || !user || userType !== 'admin') return;
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchSettings();
  }, [authLoading, user, userType]);

  const fetchSettings = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      // Fetch GPS range
      const gpsResponse = await adminAPI.getGpsRange();
      setGpsRange(gpsResponse.data.range_meters);
      setOriginalGpsRange(gpsResponse.data.range_meters);
      setDefaultRange(gpsResponse.data.default);
      
      // Fetch salary rates
      try {
        const salaryResponse = await adminAPI.getSalaryRates();
        const rates = salaryResponse.data.rates || {};
        setSalaryRates({
          sweeper: rates.sweeper || 0,
          gardener: rates.gardener || 0,
          security: rates.security || 0
        });
        setOriginalSalaryRates({
          sweeper: rates.sweeper || 0,
          gardener: rates.gardener || 0,
          security: rates.security || 0
        });
      } catch (err) {
        // No salary rates configured yet
        console.log('No salary rates configured yet');
      }
      
      // Fetch zone staff salary settings
      try {
        const zoneStaffResponse = await adminAPI.getZoneStaffSalarySettings();
        setZoneStaffSalary({
          electrician_salary_per_day: zoneStaffResponse.data.electrician_salary_per_day || 0,
          supervisor_salary_per_day: zoneStaffResponse.data.supervisor_salary_per_day || 0
        });
        setOriginalZoneStaffSalary({
          electrician_salary_per_day: zoneStaffResponse.data.electrician_salary_per_day || 0,
          supervisor_salary_per_day: zoneStaffResponse.data.supervisor_salary_per_day || 0
        });
      } catch (err) {
        console.log('No zone staff salary configured yet');
      }
      
      // Fetch correction permissions
      try {
        const permResponse = await adminAPI.getCorrectionPermissions();
        setActivePermissions(permResponse.data.active_permissions || []);
        setExpiredPermissions(permResponse.data.expired_permissions || []);
        setSupervisorsList(permResponse.data.supervisors || []);
        
        // Fetch pending requests
        const reqResponse = await adminAPI.getCorrectionPermissionRequests();
        setPermissionRequests(reqResponse.data.requests || []);
      } catch (err) {
        console.log('Could not fetch correction permissions');
      }
      if (isManualRefresh) {
        toast.success('Settings refreshed');
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchSettings(true);
  };

  const fetchGpsRange = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getGpsRange();
      setGpsRange(response.data.range_meters);
      setOriginalGpsRange(response.data.range_meters);
      setDefaultRange(response.data.default);
    } catch (error) {
      toast.error('Failed to load GPS settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGpsRange = async () => {
    if (gpsRange < 10 || gpsRange > 10000) {
      toast.error('GPS range must be between 10 and 10,000 meters');
      return;
    }
    
    setSaving(true);
    try {
      await adminAPI.setGpsRange(gpsRange);
      setOriginalGpsRange(gpsRange);
      toast.success(`GPS range updated to ${gpsRange} meters`);
    } catch (error) {
      toast.error('Failed to save GPS settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    setGpsRange(defaultRange);
    setSaving(true);
    try {
      await adminAPI.setGpsRange(defaultRange);
      setOriginalGpsRange(defaultRange);
      toast.success(`GPS range reset to default (${defaultRange}m)`);
    } catch (error) {
      toast.error('Failed to reset GPS settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSalaryRates = async () => {
    if (salaryRates.sweeper < 0 || salaryRates.gardener < 0 || salaryRates.security < 0) {
      toast.error('Salary rates cannot be negative');
      return;
    }
    
    setSavingSalary(true);
    try {
      await adminAPI.setSalaryRates(salaryRates);
      setOriginalSalaryRates({ ...salaryRates });
      toast.success('Salary rates saved successfully');
    } catch (error) {
      toast.error('Failed to save salary rates');
    } finally {
      setSavingSalary(false);
    }
  };

  const handleSaveZoneStaffSalary = async (applyToAll = false) => {
    if (zoneStaffSalary.electrician_salary_per_day < 0 || zoneStaffSalary.supervisor_salary_per_day < 0) {
      toast.error('Salary rates cannot be negative');
      return;
    }
    
    setSavingZoneStaffSalary(true);
    try {
      const response = await adminAPI.updateZoneStaffSalarySettings({
        ...zoneStaffSalary,
        apply_to_all: applyToAll
      });
      setOriginalZoneStaffSalary({ ...zoneStaffSalary });
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to save zone staff salary settings');
    } finally {
      setSavingZoneStaffSalary(false);
    }
  };

  // Correction permission handlers
  const handleGrantPermission = async (supervisorId = null, fromDate = null, validUntil = null) => {
    const supId = supervisorId || newPermission.supervisor_id;
    const from = fromDate || newPermission.from_date;
    const until = validUntil || newPermission.valid_until;
    
    if (!supId || !from) {
      toast.error('Please select a supervisor and "editable from" date');
      return;
    }
    
    setSavingPermission(true);
    try {
      await adminAPI.grantCorrectionPermission(supId, from, until);
      toast.success('Permission granted successfully');
      setNewPermission({ supervisor_id: '', from_date: '', valid_until: '' });
      
      // Refresh permissions and requests
      const permResponse = await adminAPI.getCorrectionPermissions();
      setActivePermissions(permResponse.data.active_permissions || []);
      setExpiredPermissions(permResponse.data.expired_permissions || []);
      const reqResponse = await adminAPI.getCorrectionPermissionRequests();
      setPermissionRequests(reqResponse.data.requests || []);
    } catch (error) {
      toast.error('Failed to grant permission');
    } finally {
      setSavingPermission(false);
    }
  };

  const handleRevokePermission = async (supervisorId) => {
    if (!window.confirm('Are you sure you want to revoke this permission?')) return;
    
    try {
      await adminAPI.revokeCorrectionPermission(supervisorId);
      toast.success('Permission revoked');
      setActivePermissions(prev => prev.filter(p => p.supervisor_id !== supervisorId));
    } catch (error) {
      toast.error('Failed to revoke permission');
    }
  };

  const hasGpsChanges = gpsRange !== originalGpsRange;
  const hasSalaryChanges = 
    salaryRates.sweeper !== originalSalaryRates.sweeper ||
    salaryRates.gardener !== originalSalaryRates.gardener ||
    salaryRates.security !== originalSalaryRates.security;
  const hasZoneStaffSalaryChanges = 
    zoneStaffSalary.electrician_salary_per_day !== originalZoneStaffSalary.electrician_salary_per_day ||
    zoneStaffSalary.supervisor_salary_per_day !== originalZoneStaffSalary.supervisor_salary_per_day;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white p-4 shadow-md">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="hover:opacity-80">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Admin Settings
              </h1>
              <p className="text-sm opacity-90">System Configuration</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="h-10 px-4 rounded-lg bg-white/20 hover:bg-white/30 disabled:opacity-50 flex items-center gap-2"
            data-testid="refresh-settings-btn"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 md:p-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-slate-600">Loading settings...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Settings Navigation Cards */}
            <div className="grid grid-cols-2 gap-4">
              {/* GPS Settings Card */}
              <button
                onClick={() => toggleSection('gps')}
                className={`bg-white rounded-xl shadow-sm p-6 text-left hover:shadow-md transition-shadow border-2 ${expandedSection === 'gps' ? 'border-blue-500' : 'border-transparent'}`}
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                  <MapPin className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg text-slate-800">GPS Location</h3>
                <p className="text-sm text-slate-500 mt-1">Range: {gpsRange}m</p>
              </button>
              
              {/* Worker Salary Card */}
              <button
                onClick={() => toggleSection('workerSalary')}
                className={`bg-white rounded-xl shadow-sm p-6 text-left hover:shadow-md transition-shadow border-2 ${expandedSection === 'workerSalary' ? 'border-green-500' : 'border-transparent'}`}
              >
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                  <IndianRupee className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-bold text-lg text-slate-800">Worker Salary</h3>
                <p className="text-sm text-slate-500 mt-1">Daily rate config</p>
              </button>
              
              {/* Zone Staff Salary Card */}
              <button
                onClick={() => toggleSection('zoneStaffSalary')}
                className={`bg-white rounded-xl shadow-sm p-6 text-left hover:shadow-md transition-shadow border-2 ${expandedSection === 'zoneStaffSalary' ? 'border-orange-500' : 'border-transparent'}`}
              >
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
                  <IndianRupee className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-bold text-lg text-slate-800">Zone Staff Salary</h3>
                <p className="text-sm text-slate-500 mt-1">Electrician & Supervisor</p>
              </button>
              
              {/* Correction Permissions Card */}
              <button
                onClick={() => toggleSection('corrections')}
                className={`bg-white rounded-xl shadow-sm p-6 text-left hover:shadow-md transition-shadow border-2 ${expandedSection === 'corrections' ? 'border-purple-500' : 'border-transparent'} relative`}
              >
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                  <Clock className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-bold text-lg text-slate-800">Attendance Corrections</h3>
                <p className="text-sm text-slate-500 mt-1">Permission requests</p>
                {permissionRequests.length > 0 && (
                  <span className="absolute top-4 right-4 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {permissionRequests.length}
                  </span>
                )}
              </button>
            </div>
            
            {/* Expanded Section Content */}
            {expandedSection === 'gps' && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
                <div className="flex items-center gap-3">
                  <MapPin className="w-6 h-6" />
                  <h2 className="text-lg font-bold">GPS Location Verification</h2>
                </div>
              </div>
              <div className="p-6">
                <div className="mb-6">
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    GPS Range (meters)
                  </label>
                  <p className="text-sm text-slate-500 mb-4">
                    Supervisors within this range of the park's GPS coordinates will be marked as "Within Range". 
                    Those outside will be flagged as "Out of Range" in the Supervisor Monitoring reports.
                  </p>
                  
                  <div className="flex items-center gap-4">
                    <input
                      type="number"
                      value={gpsRange}
                      onChange={(e) => setGpsRange(parseInt(e.target.value) || 0)}
                      min={10}
                      max={10000}
                      className="w-32 h-12 px-4 text-lg font-bold text-center border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:ring-0"
                      data-testid="gps-range-input"
                    />
                    <span className="text-slate-600 font-medium">meters</span>
                    
                    <div className="flex-1" />
                    
                    <button
                      onClick={handleResetToDefault}
                      disabled={saving || gpsRange === defaultRange}
                      className="h-10 px-4 border-2 border-slate-300 rounded-lg font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2"
                      data-testid="reset-gps-btn"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reset to Default ({defaultRange}m)
                    </button>
                  </div>
                </div>

                {/* Visual Range Indicator */}
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-slate-700 mb-3">Range Visualization</h3>
                  <div className="flex items-center gap-4">
                    <div className="relative w-32 h-32">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div 
                          className="bg-green-200 rounded-full border-2 border-green-500 flex items-center justify-center"
                          style={{ 
                            width: `${Math.min(100, Math.max(30, gpsRange / 100 * 10))}%`,
                            height: `${Math.min(100, Math.max(30, gpsRange / 100 * 10))}%`
                          }}
                        >
                          <div className="w-3 h-3 bg-blue-600 rounded-full" />
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-slate-600">
                      <p><span className="font-semibold text-green-600">Green Zone:</span> Within {gpsRange}m - Valid</p>
                      <p><span className="font-semibold text-red-600">Outside:</span> Beyond {gpsRange}m - Flagged</p>
                      <p className="mt-2 text-xs text-slate-500">
                        Tip: Use 100-200m for strict verification, 500-1000m for lenient verification.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick Presets */}
                <div className="mb-6">
                  <h3 className="font-semibold text-slate-700 mb-3">Quick Presets</h3>
                  <div className="flex flex-wrap gap-2">
                    {[50, 100, 200, 500, 1000].map((preset) => (
                      <button
                        key={preset}
                        onClick={() => setGpsRange(preset)}
                        className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                          gpsRange === preset
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                        data-testid={`preset-${preset}m`}
                      >
                        {preset}m
                      </button>
                    ))}
                  </div>
                </div>

                {/* Save Button */}
                {hasGpsChanges && (
                  <div className="flex justify-end pt-4 border-t">
                    <button
                      onClick={handleSaveGpsRange}
                      disabled={saving}
                      className="h-12 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-bold flex items-center gap-2"
                      data-testid="save-gps-settings-btn"
                    >
                      {saving ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Salary Configuration */}
            {expandedSection === 'workerSalary' && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4">
                <div className="flex items-center gap-3">
                  <IndianRupee className="w-6 h-6" />
                  <h2 className="text-lg font-bold">Worker Salary Configuration</h2>
                </div>
              </div>
              
              <div className="p-6">
                <p className="text-sm text-slate-500 mb-6">
                  Configure the daily wage rate for each worker category. These rates will be used in the Monthly Zone Attendance reports to calculate salary payable.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  {/* Sweeper */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Sweeper (per day)
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">₹</span>
                      <input
                        type="number"
                        value={salaryRates.sweeper}
                        onChange={(e) => setSalaryRates(prev => ({ ...prev, sweeper: parseInt(e.target.value) || 0 }))}
                        min={0}
                        className="flex-1 h-12 px-4 text-lg font-bold text-center border-2 border-slate-300 rounded-lg focus:border-green-600 focus:ring-0"
                        data-testid="sweeper-rate-input"
                      />
                    </div>
                  </div>
                  
                  {/* Gardener */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Gardener (per day)
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">₹</span>
                      <input
                        type="number"
                        value={salaryRates.gardener}
                        onChange={(e) => setSalaryRates(prev => ({ ...prev, gardener: parseInt(e.target.value) || 0 }))}
                        min={0}
                        className="flex-1 h-12 px-4 text-lg font-bold text-center border-2 border-slate-300 rounded-lg focus:border-green-600 focus:ring-0"
                        data-testid="gardener-rate-input"
                      />
                    </div>
                  </div>
                  
                  {/* Security */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Security (per day)
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">₹</span>
                      <input
                        type="number"
                        value={salaryRates.security}
                        onChange={(e) => setSalaryRates(prev => ({ ...prev, security: parseInt(e.target.value) || 0 }))}
                        min={0}
                        className="flex-1 h-12 px-4 text-lg font-bold text-center border-2 border-slate-300 rounded-lg focus:border-green-600 focus:ring-0"
                        data-testid="security-rate-input"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Current Rates Display */}
                {(originalSalaryRates.sweeper > 0 || originalSalaryRates.gardener > 0 || originalSalaryRates.security > 0) && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold text-green-800 mb-2">Current Saved Rates</h4>
                    <div className="flex gap-4 text-sm text-green-700">
                      <span>Sweeper: ₹{originalSalaryRates.sweeper}/day</span>
                      <span>Gardener: ₹{originalSalaryRates.gardener}/day</span>
                      <span>Security: ₹{originalSalaryRates.security}/day</span>
                    </div>
                  </div>
                )}
                
                {/* Save Button */}
                {hasSalaryChanges && (
                  <div className="flex justify-end pt-4 border-t">
                    <button
                      onClick={handleSaveSalaryRates}
                      disabled={savingSalary}
                      className="h-12 px-6 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg font-bold flex items-center gap-2"
                      data-testid="save-salary-btn"
                    >
                      {savingSalary ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          Save Salary Rates
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Zone Staff Salary Configuration */}
            {expandedSection === 'zoneStaffSalary' && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-yellow-600 to-orange-600 text-white p-4">
                <div className="flex items-center gap-3">
                  <IndianRupee className="w-6 h-6" />
                  <h2 className="text-lg font-bold">Zone Staff Salary (Electrician & Supervisor)</h2>
                </div>
              </div>
              
              <div className="p-6">
                <p className="text-sm text-slate-500 mb-6">
                  Configure the daily wage rate for Electricians and Supervisors. These staff are assigned zone-wise and their attendance is tracked via WhatsApp.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Electrician */}
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Electrician (per day)
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">₹</span>
                      <input
                        type="number"
                        value={zoneStaffSalary.electrician_salary_per_day}
                        onChange={(e) => setZoneStaffSalary(prev => ({ ...prev, electrician_salary_per_day: parseInt(e.target.value) || 0 }))}
                        min={0}
                        className="flex-1 h-12 px-4 text-lg font-bold text-center border-2 border-slate-300 rounded-lg focus:border-yellow-600 focus:ring-0"
                        data-testid="electrician-rate-input"
                      />
                    </div>
                  </div>
                  
                  {/* Supervisor */}
                  <div className="bg-orange-50 rounded-lg p-4">
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Supervisor (per day)
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">₹</span>
                      <input
                        type="number"
                        value={zoneStaffSalary.supervisor_salary_per_day}
                        onChange={(e) => setZoneStaffSalary(prev => ({ ...prev, supervisor_salary_per_day: parseInt(e.target.value) || 0 }))}
                        min={0}
                        className="flex-1 h-12 px-4 text-lg font-bold text-center border-2 border-slate-300 rounded-lg focus:border-orange-600 focus:ring-0"
                        data-testid="supervisor-rate-input"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Current Rates Display */}
                {(originalZoneStaffSalary.electrician_salary_per_day > 0 || originalZoneStaffSalary.supervisor_salary_per_day > 0) && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold text-yellow-800 mb-2">Current Saved Rates</h4>
                    <div className="flex gap-4 text-sm text-yellow-700">
                      <span>Electrician: ₹{originalZoneStaffSalary.electrician_salary_per_day}/day</span>
                      <span>Supervisor: ₹{originalZoneStaffSalary.supervisor_salary_per_day}/day</span>
                    </div>
                  </div>
                )}
                
                {/* Save Buttons */}
                {hasZoneStaffSalaryChanges && (
                  <div className="flex flex-wrap gap-3 justify-end pt-4 border-t">
                    <button
                      onClick={() => handleSaveZoneStaffSalary(false)}
                      disabled={savingZoneStaffSalary}
                      className="h-12 px-6 bg-slate-600 hover:bg-slate-700 disabled:bg-slate-400 text-white rounded-lg font-bold flex items-center gap-2"
                      data-testid="save-zone-staff-salary-btn"
                    >
                      {savingZoneStaffSalary ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          Save Settings Only
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleSaveZoneStaffSalary(true)}
                      disabled={savingZoneStaffSalary}
                      className="h-12 px-6 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white rounded-lg font-bold flex items-center gap-2"
                      data-testid="apply-zone-staff-salary-btn"
                    >
                      {savingZoneStaffSalary ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Applying...
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          Save & Apply to All Staff
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Correction Permissions (3-Day Limit) */}
            {expandedSection === 'corrections' && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-6 h-6" />
                  <h2 className="text-lg font-bold">Attendance Correction Permissions</h2>
                </div>
              </div>
              
              <div className="p-6">
                <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    <strong>3-Day Edit Rule:</strong> Supervisors can only correct attendance for the last 3 days by default. 
                    Use this section to grant permission for dates beyond this limit.
                  </p>
                </div>
                
                {/* Pending Requests */}
                {permissionRequests.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-sm">Pending Requests ({permissionRequests.length})</span>
                    </h3>
                    <div className="space-y-2">
                      {permissionRequests.map(req => (
                        <div key={req.id} className="border border-red-200 bg-red-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <div className="font-semibold text-slate-800">
                                {req.supervisor_name} ({req.supervisor_id})
                              </div>
                              <div className="text-sm text-slate-500">
                                Requested for: <strong>{req.requested_date}</strong>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <label className="text-xs text-slate-600">Valid until:</label>
                            <input
                              type="date"
                              id={`valid-until-${req.id}`}
                              defaultValue={new Date(Date.now() + 7*24*60*60*1000).toISOString().split('T')[0]}
                              className="h-8 px-2 border border-slate-300 rounded text-sm"
                            />
                            <button
                              onClick={() => {
                                const validUntil = document.getElementById(`valid-until-${req.id}`).value;
                                handleGrantPermission(req.supervisor_id, req.requested_date, validUntil);
                              }}
                              disabled={savingPermission}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded font-semibold text-sm"
                            >
                              Approve
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Grant Permission Form */}
                <div className="mb-6 bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-700 mb-3">Grant New Permission</h3>
                  <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[180px]">
                      <label className="text-xs text-slate-600 block mb-1">Supervisor</label>
                      <select
                        value={newPermission.supervisor_id}
                        onChange={(e) => setNewPermission(prev => ({ ...prev, supervisor_id: e.target.value }))}
                        className="h-10 px-3 border-2 border-slate-300 rounded-lg w-full"
                      >
                        <option value="">Select...</option>
                        {supervisorsList.map(s => (
                          <option key={s.supervisor_id} value={s.supervisor_id}>
                            {s.name} ({s.supervisor_id})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 block mb-1">Month to edit (any date)</label>
                      <input
                        type="date"
                        value={newPermission.from_date}
                        onChange={(e) => setNewPermission(prev => ({ ...prev, from_date: e.target.value }))}
                        className="h-10 px-3 border-2 border-slate-300 rounded-lg"
                      />
                      <p className="text-xs text-slate-400 mt-1">Will allow editing from 1st of this month</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 block mb-1">Edit until date</label>
                      <input
                        type="date"
                        value={newPermission.valid_until}
                        onChange={(e) => setNewPermission(prev => ({ ...prev, valid_until: e.target.value }))}
                        className="h-10 px-3 border-2 border-slate-300 rounded-lg"
                        placeholder={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <button
                      onClick={() => handleGrantPermission()}
                      disabled={savingPermission || !newPermission.supervisor_id || !newPermission.from_date}
                      className="h-10 px-4 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white rounded-lg font-semibold flex items-center gap-2"
                    >
                      {savingPermission ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                      Grant
                    </button>
                  </div>
                </div>
                
                {/* Tabs for Active/Expired */}
                <div className="flex gap-2 mb-4 border-b">
                  <button
                    onClick={() => setPermissionTab('active')}
                    className={`px-4 py-2 font-semibold text-sm border-b-2 -mb-px ${
                      permissionTab === 'active' 
                        ? 'border-purple-600 text-purple-600' 
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Active ({activePermissions.length})
                  </button>
                  <button
                    onClick={() => setPermissionTab('expired')}
                    className={`px-4 py-2 font-semibold text-sm border-b-2 -mb-px ${
                      permissionTab === 'expired' 
                        ? 'border-slate-600 text-slate-600' 
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Past ({expiredPermissions.length})
                  </button>
                </div>
                
                {/* Active Permissions */}
                {permissionTab === 'active' && (
                  activePermissions.length > 0 ? (
                    <div className="space-y-2">
                      {activePermissions.map(perm => (
                        <div key={perm.supervisor_id} className="border border-green-200 bg-green-50 rounded-lg p-3 flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-slate-800">
                              {perm.supervisor_name} ({perm.supervisor_id})
                            </div>
                            <div className="text-sm text-slate-500">
                              Can edit: <strong className="text-purple-700">{perm.from_date}</strong>
                              {perm.valid_until && (
                                <span> to <strong className="text-green-700">{perm.valid_until}</strong></span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRevokePermission(perm.supervisor_id)}
                            className="text-red-600 hover:text-red-800 p-2"
                            title="Revoke permission"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">No active permissions</p>
                  )
                )}
                
                {/* Expired Permissions */}
                {permissionTab === 'expired' && (
                  expiredPermissions.length > 0 ? (
                    <div className="space-y-2">
                      {expiredPermissions.map(perm => (
                        <div key={perm.supervisor_id} className="border border-slate-200 bg-slate-50 rounded-lg p-3 opacity-70">
                          <div className="font-semibold text-slate-600">
                            {perm.supervisor_name} ({perm.supervisor_id})
                          </div>
                          <div className="text-sm text-slate-400">
                            Was editable from: {perm.from_date}
                            {perm.valid_until && <span className="ml-2">• Expired: {perm.valid_until}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">No past permissions</p>
                  )
                )}
              </div>
            </div>
            )}

            {/* Info Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h3 className="font-bold text-blue-800 mb-2">How GPS Verification Works</h3>
              <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                <li>When a supervisor starts an inspection, their GPS location is captured</li>
                <li>The system calculates the distance from the park's registered coordinates</li>
                <li>Results are shown in the Supervisor Monitoring dashboard</li>
                <li>Supervisors outside the range are flagged but can still complete inspections</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
